/**
 * VEDANGJYOTISHAM V6.0 — CLASSICAL GOCHAR VEDHA (OBSTRUCTION) ENGINE
 * 
 * Classical Reference: Phaladeepika (Chapter 26, Slokas 1–20) & Brihat Samhita
 * 
 * Invariant:
 * 1. ZERO duplicate ephemeris calculation: Evaluates deterministic rules over authoritative transit positions.
 * 2. Operates relative to Chandra Lagna (Natal Moon Sign).
 * 3. Incorporates classical filial exemptions:
 *    - Sun (Surya) and Saturn (Shani) cannot cause Vedha to each other (Pita-Putra Sambandha).
 *    - Moon (Chandra) and Mercury (Budha) cannot cause Vedha to each other (Mata-Putra Sambandha).
 * 4. Distinctly separates Calculated Fact from Astrological Interpretation.
 */

import { PlanetName, ZodiacSign } from '../types/kundali';

export function calculateHouseDistance(natalSignIdx: number, transitSignIdx: number): number {
  let house = transitSignIdx - natalSignIdx + 1;
  if (house <= 0) house += 12;
  return house;
}

export interface ClassicalVedhaRule {
  favorableHouse: number;
  obstructingHouse: number;
}

/**
 * Classical Benefic Transit Houses from Moon and their paired Vedha (Obstructing) Houses.
 * Source: Phaladeepika 26.1 - 26.20
 */
export const CLASSICAL_VEDHA_PAIRS: Record<PlanetName, ClassicalVedhaRule[]> = {
  Sun: [
    { favorableHouse: 3, obstructingHouse: 9 },
    { favorableHouse: 6, obstructingHouse: 12 },
    { favorableHouse: 10, obstructingHouse: 4 },
    { favorableHouse: 11, obstructingHouse: 5 }
  ],
  Moon: [
    { favorableHouse: 1, obstructingHouse: 5 },
    { favorableHouse: 3, obstructingHouse: 9 },
    { favorableHouse: 6, obstructingHouse: 12 },
    { favorableHouse: 7, obstructingHouse: 2 },
    { favorableHouse: 10, obstructingHouse: 4 },
    { favorableHouse: 11, obstructingHouse: 8 }
  ],
  Mars: [
    { favorableHouse: 3, obstructingHouse: 12 },
    { favorableHouse: 6, obstructingHouse: 9 },
    { favorableHouse: 11, obstructingHouse: 5 }
  ],
  Mercury: [
    { favorableHouse: 2, obstructingHouse: 5 },
    { favorableHouse: 4, obstructingHouse: 3 },
    { favorableHouse: 6, obstructingHouse: 12 },
    { favorableHouse: 8, obstructingHouse: 11 },
    { favorableHouse: 10, obstructingHouse: 8 },
    { favorableHouse: 11, obstructingHouse: 7 }
  ],
  Jupiter: [
    { favorableHouse: 2, obstructingHouse: 12 },
    { favorableHouse: 5, obstructingHouse: 4 },
    { favorableHouse: 7, obstructingHouse: 3 },
    { favorableHouse: 9, obstructingHouse: 10 },
    { favorableHouse: 11, obstructingHouse: 8 }
  ],
  Venus: [
    { favorableHouse: 1, obstructingHouse: 8 },
    { favorableHouse: 2, obstructingHouse: 7 },
    { favorableHouse: 3, obstructingHouse: 1 },
    { favorableHouse: 4, obstructingHouse: 10 },
    { favorableHouse: 5, obstructingHouse: 9 },
    { favorableHouse: 8, obstructingHouse: 5 },
    { favorableHouse: 9, obstructingHouse: 11 },
    { favorableHouse: 11, obstructingHouse: 6 },
    { favorableHouse: 12, obstructingHouse: 3 }
  ],
  Saturn: [
    { favorableHouse: 3, obstructingHouse: 12 },
    { favorableHouse: 6, obstructingHouse: 9 },
    { favorableHouse: 11, obstructingHouse: 5 }
  ],
  Rahu: [
    { favorableHouse: 3, obstructingHouse: 12 },
    { favorableHouse: 6, obstructingHouse: 9 },
    { favorableHouse: 11, obstructingHouse: 5 }
  ],
  Ketu: [
    { favorableHouse: 3, obstructingHouse: 12 },
    { favorableHouse: 6, obstructingHouse: 9 },
    { favorableHouse: 11, obstructingHouse: 5 }
  ],
  Ascendant: []
};

/**
 * Checks classical filial exemption between two planets:
 * 1. Sun <-> Saturn (Father & Son)
 * 2. Moon <-> Mercury (Mother & Son)
 */
export function isVedhaExempted(planetA: PlanetName, planetB: PlanetName): { isExempt: boolean; reason?: string } {
  if ((planetA === 'Sun' && planetB === 'Saturn') || (planetA === 'Saturn' && planetB === 'Sun')) {
    return {
      isExempt: true,
      reason: 'Sun and Saturn are exempt from mutual Vedha obstruction (Classical Father-Son relation / Pita-Putra exemption).'
    };
  }
  if ((planetA === 'Moon' && planetB === 'Mercury') || (planetA === 'Mercury' && planetB === 'Moon')) {
    return {
      isExempt: true,
      reason: 'Moon and Mercury are exempt from mutual Vedha obstruction (Classical Mother-Son relation / Mata-Putra exemption).'
    };
  }
  return { isExempt: false };
}

export interface TransitPlanetContext {
  planet: PlanetName;
  sign: ZodiacSign;
  signIndex: number;
}

export interface VedhaStatusResult {
  planet: PlanetName;
  houseFromMoon: number;
  isFavorableFromMoon: boolean;
  hasVedha: boolean;
  obstructingPlanet: PlanetName | null;
  obstructingHouseFromMoon: number | null;
  obstructingSign: ZodiacSign | null;
  pairedRule: ClassicalVedhaRule | null;
  isExempted: boolean;
  exemptionReason?: string;
  classicalCitation: string;
  factualStatus: 'FAVORABLE_UNOBSTRUCTED' | 'FAVORABLE_VEDHA_OBSTRUCTED' | 'NEUTRAL_OR_DISCIPLINARY';
  interpretationEn: string;
  interpretationHi: string;
}

/**
 * Deterministically evaluates the Vedha status of a specific transit planet relative to
 * all active transit positions from the Natal Moon sign.
 */
export function evaluateGocharVedha(
  targetPlanet: PlanetName,
  moonSignIndex: number,
  allLiveTransits: TransitPlanetContext[]
): VedhaStatusResult {
  const targetTransit = allLiveTransits.find(t => t.planet === targetPlanet);
  if (!targetTransit) {
    return {
      planet: targetPlanet,
      houseFromMoon: 1,
      isFavorableFromMoon: false,
      hasVedha: false,
      obstructingPlanet: null,
      obstructingHouseFromMoon: null,
      obstructingSign: null,
      pairedRule: null,
      isExempted: false,
      classicalCitation: 'Phaladeepika Ch. 26',
      factualStatus: 'NEUTRAL_OR_DISCIPLINARY',
      interpretationEn: 'No transit position found for evaluation.',
      interpretationHi: 'गोचर गणना हेतु स्थिति उपलब्ध नहीं है।'
    };
  }

  const houseFromMoon = calculateHouseDistance(moonSignIndex, targetTransit.signIndex);
  const rules = CLASSICAL_VEDHA_PAIRS[targetPlanet] || [];
  const matchedRule = rules.find(r => r.favorableHouse === houseFromMoon) || null;

  // If not a classical favorable house for this planet, Vedha does not apply to favorable results
  if (!matchedRule) {
    return {
      planet: targetPlanet,
      houseFromMoon,
      isFavorableFromMoon: false,
      hasVedha: false,
      obstructingPlanet: null,
      obstructingHouseFromMoon: null,
      obstructingSign: null,
      pairedRule: null,
      isExempted: false,
      classicalCitation: 'Phaladeepika Ch. 26, Sloka 1–15',
      factualStatus: 'NEUTRAL_OR_DISCIPLINARY',
      interpretationEn: `${targetPlanet} transits House ${houseFromMoon} from Moon (Neutral/Disciplinary classical transit). Vedha applies to benefic transit modification.`,
      interpretationHi: `${targetPlanet} चंद्र से ${houseFromMoon}वें भाव में गोचरस्थ है (तटस्थ/अनुशासन भाव)। वेध नियम शुभ गोचर फल पर प्रभावी होता है।`
    };
  }

  // Look for any planet transiting in the paired obstructing house from Moon
  const obstructingCandidates = allLiveTransits.filter(t => {
    if (t.planet === targetPlanet || t.planet === 'Ascendant') return false;
    const candidateHouse = calculateHouseDistance(moonSignIndex, t.signIndex);
    return candidateHouse === matchedRule.obstructingHouse;
  });

  if (obstructingCandidates.length === 0) {
    return {
      planet: targetPlanet,
      houseFromMoon,
      isFavorableFromMoon: true,
      hasVedha: false,
      obstructingPlanet: null,
      obstructingHouseFromMoon: matchedRule.obstructingHouse,
      obstructingSign: null,
      pairedRule: matchedRule,
      isExempted: false,
      classicalCitation: 'Phaladeepika Ch. 26, Sloka 1–15',
      factualStatus: 'FAVORABLE_UNOBSTRUCTED',
      interpretationEn: `Favorable transit in House ${houseFromMoon} from Moon with NO obstructing planet in House ${matchedRule.obstructingHouse}. Benefic potential manifests unobstructed.`,
      interpretationHi: `चंद्र से ${houseFromMoon}वें भाव में शुभ गोचर, एवं ${matchedRule.obstructingHouse}वें भाव में कोई वेध ग्रह नहीं है। शुभ फल निर्बाध रूप से प्राप्त होगा।`
    };
  }

  // Evaluate exemptions for obstructing planets
  for (const candidate of obstructingCandidates) {
    const exemption = isVedhaExempted(targetPlanet, candidate.planet);
    if (exemption.isExempt) {
      return {
        planet: targetPlanet,
        houseFromMoon,
        isFavorableFromMoon: true,
        hasVedha: false,
        obstructingPlanet: candidate.planet,
        obstructingHouseFromMoon: matchedRule.obstructingHouse,
        obstructingSign: candidate.sign,
        pairedRule: matchedRule,
        isExempted: true,
        exemptionReason: exemption.reason,
        classicalCitation: 'Phaladeepika Ch. 26, Sloka 19–20',
        factualStatus: 'FAVORABLE_UNOBSTRUCTED',
        interpretationEn: `Favorable transit in House ${houseFromMoon} from Moon. ${candidate.planet} transits the paired House ${matchedRule.obstructingHouse}, but classical exemption applies (${exemption.reason}). Benefic transit remains active.`,
        interpretationHi: `चंद्र से ${houseFromMoon}वें भाव में शुभ गोचर। ${candidate.planet} वेध भाव (${matchedRule.obstructingHouse}) में स्थित है परंतु शास्त्रीय छूट के कारण वेध निष्प्रभावी है।`
      };
    }
  }

  // Classical Vedha Obstruction Identified
  const firstObstructing = obstructingCandidates[0];
  return {
    planet: targetPlanet,
    houseFromMoon,
    isFavorableFromMoon: true,
    hasVedha: true,
    obstructingPlanet: firstObstructing.planet,
    obstructingHouseFromMoon: matchedRule.obstructingHouse,
    obstructingSign: firstObstructing.sign,
    pairedRule: matchedRule,
    isExempted: false,
    classicalCitation: 'Phaladeepika Ch. 26, Sloka 1–15',
    factualStatus: 'FAVORABLE_VEDHA_OBSTRUCTED',
    interpretationEn: `Classical Vedha condition identified: ${targetPlanet}'s benefic transit in House ${houseFromMoon} from Moon is obstructed by ${firstObstructing.planet} transiting in House ${matchedRule.obstructingHouse} (${firstObstructing.sign}). Classical obstruction modifies the usual transit indication.`,
    interpretationHi: `शास्त्रीय वेध स्थिति: चंद्र से ${houseFromMoon}वें भाव में ${targetPlanet} का शुभ गोचर, ${matchedRule.obstructingHouse}वें भाव (${firstObstructing.sign}) में स्थित ${firstObstructing.planet} द्वारा वेधित है। यह शास्त्रीय अवरोध गोचर फल को संशोधित करता है।`
  };
}

/**
 * Batch evaluates Vedha for all live planets from natal Moon.
 */
export function evaluateAllGocharVedha(
  moonSignIndex: number,
  allLiveTransits: TransitPlanetContext[]
): Record<PlanetName, VedhaStatusResult> {
  const result: Partial<Record<PlanetName, VedhaStatusResult>> = {};
  for (const item of allLiveTransits) {
    if (item.planet !== 'Ascendant') {
      result[item.planet] = evaluateGocharVedha(item.planet, moonSignIndex, allLiveTransits);
    }
  }
  return result as Record<PlanetName, VedhaStatusResult>;
}
